/**
 * Brera · Agregado rápido desde la tarjeta de producto
 * - Una sola variante: agrega directo y muestra un aviso con "Ver carrito".
 * - Varias variantes: abre una hoja (derecha en computador, desde abajo en celular) con las opciones.
 * Al agregar emite 'cart:updated', actualiza el contador del carrito ([data-cart-count]) y deja un ✓ en el botón
 * de la tarjeta por un momento (clase is-added; mientras agrega lleva is-loading).
 * Velocidad: el mismo pedido a /cart/add.js trae ya armadas las secciones del carrito ([data-cart-root]
 * [data-section-id]) y van en el evento (detail.sections), así assets/cart.js abre el carrito lateral sin
 * pedir nada más. El contador se actualiza después, sin hacer esperar.
 * Textos y formato de precio en window.Brera (layout/theme.liquid).
 */
(() => {
  if (window.BreraQuickAdd) return;
  const B = window.Brera || {};
  const root = (B.routes && B.routes.root) || '/';
  const t = Object.assign(
    { add: 'Agregar al carrito', added: 'Agregado al carrito', soldOut: 'Agotado', choose: 'Elige', viewCart: 'Ver carrito', viewFull: 'Ver detalles completos', close: 'Cerrar', error: 'No se pudo agregar. Intenta de nuevo.' },
    B.strings || {}
  );

  const formatMoney = (cents) => {
    const m = B.money || {};
    const fmt = m.format || '${{amount}}';
    const value = (cents || 0) / 100;
    const build = (decimals, thousands, decimal) => {
      let [int, dec] = value.toFixed(decimals).split('.');
      int = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      if (dec && m.decimals !== 'always' && /^0+$/.test(dec)) dec = '';
      return dec ? int + decimal + dec : int;
    };
    const out = fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => {
      switch (k) {
        case 'amount_no_decimals': return build(0, ',', '.');
        case 'amount_with_comma_separator': return build(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return build(0, '.', ',');
        case 'amount_with_apostrophe_separator': return build(2, "'", '.');
        default: return build(2, ',', '.');
      }
    });
    return m.code ? out + ' ' + m.code : out;
  };

  const updateCount = async () => {
    try {
      const cart = await fetch(root + 'cart.js').then((r) => r.json());
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.classList.toggle('is-empty', cart.item_count === 0);
      });
      document.dispatchEvent(new CustomEvent('cart:count', { detail: { cart } }));
    } catch (e) {}
  };

  // Secciones del carrito que hay en la página (carrito lateral y/o página del carrito)
  const cartSections = () => [...new Set([...document.querySelectorAll('[data-cart-root][data-section-id]')].map((el) => el.dataset.sectionId))];

  const addToCart = async (id) => {
    const sections = cartSections();
    const body = { items: [{ id: Number(id), quantity: 1 }] };
    if (sections.length) {
      body.sections = sections;
      body.sections_url = window.location.pathname;
    }
    const res = await fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || data.message || t.error);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { sections: data.sections || null, added: data } }));
    updateCount();
    return data;
  };

  let toastTimer;
  const toast = (html) => {
    let el = document.querySelector('.quick-add-toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'quick-add-toast';
      el.setAttribute('role', 'status');
      document.body.appendChild(el);
    }
    el.innerHTML = html;
    requestAnimationFrame(() => el.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 3500);
  };

  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const sizedImg = (src, w) => (src ? src + (src.includes('?') ? '&' : '?') + 'width=' + w : '');

  class Sheet {
    constructor() {
      this.el = document.createElement('dialog');
      this.el.className = 'quick-add-sheet';
      this.el.setAttribute('aria-label', t.add);
      this.el.innerHTML = `
        <div class="quick-add-sheet__inner">
          <div class="quick-add-sheet__top"><button type="button" class="quick-add-sheet__close" aria-label="${esc(t.close)}"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg></button></div>
          <div class="quick-add-sheet__body"></div>
          <div class="quick-add-sheet__foot">
            <button type="button" class="button quick-add-sheet__submit"></button>
            <p class="quick-add-sheet__msg" role="status"></p>
          </div>
        </div>`;
      document.body.appendChild(this.el);
      this.body = this.el.querySelector('.quick-add-sheet__body');
      this.submit = this.el.querySelector('.quick-add-sheet__submit');
      this.msg = this.el.querySelector('.quick-add-sheet__msg');
      this.el.querySelector('.quick-add-sheet__close').addEventListener('click', () => this.close());
      this.el.addEventListener('click', (e) => { if (e.target === this.el) this.close(); });
      this.el.addEventListener('cancel', (e) => { e.preventDefault(); this.close(); });
      this.submit.addEventListener('click', () => this.add());
      this.body.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-option-index]');
        if (!btn) return;
        this.selected[btn.dataset.optionIndex] = btn.dataset.value;
        this.render();
      });
    }

    async open(handle, url) {
      this.msg.textContent = '';
      this.msg.classList.remove('is-error');
      const product = await fetch(`${root}products/${handle}.js`).then((r) => r.json());
      this.product = product;
      this.url = url || product.url;
      const first = product.variants.find((v) => v.available) || product.variants[0];
      this.selected = first.options.slice();
      this.render();
      this.el.showModal();
      requestAnimationFrame(() => this.el.classList.add('is-open'));
    }

    close() {
      this.el.classList.remove('is-open');
      setTimeout(() => this.el.close(), 300);
    }

    variant() {
      return this.product.variants.find((v) => v.options.every((o, i) => o === this.selected[i]));
    }

    render() {
      const p = this.product;
      const v = this.variant();
      const img = (v && v.featured_image && v.featured_image.src) || p.featured_image;
      const price = v ? v.price : p.price;
      const compare = v ? v.compare_at_price : p.compare_at_price;
      const opts = p.options
        .map((opt, i) => {
          const values = opt.values
            .map((val) => {
              const test = this.selected.slice();
              test[i] = val;
              const match = p.variants.find((x) => x.options.every((o, j) => o === test[j]));
              const unavailable = !match || !match.available;
              return `<button type="button" class="quick-add-sheet__value${unavailable ? ' is-unavailable' : ''}" data-option-index="${i}" data-value="${esc(val)}" aria-pressed="${this.selected[i] === val}">${esc(val)}</button>`;
            })
            .join('');
          return `<div class="quick-add-sheet__option"><span class="quick-add-sheet__legend label">${esc(opt.name)}: <strong>${esc(this.selected[i])}</strong></span><div class="quick-add-sheet__values">${values}</div></div>`;
        })
        .join('');
      this.body.innerHTML = `
        <div class="quick-add-sheet__product">
          ${img ? `<img src="${esc(sizedImg(img, 300))}" alt="" width="96" height="128">` : '<span></span>'}
          <div>
            <p class="quick-add-sheet__title">${esc(p.title)}</p>
            <div class="price${compare > price ? ' price--on-sale' : ''}">
              <span class="price__current">${formatMoney(price)}</span>
              ${compare > price ? `<s class="price__compare">${formatMoney(compare)}</s>` : ''}
            </div>
            <a class="quick-add-sheet__full" href="${esc(this.url)}">${esc(t.viewFull)}</a>
          </div>
        </div>
        ${opts}`;
      const ok = v && v.available;
      this.submit.disabled = !ok;
      this.submit.textContent = !v ? t.choose : ok ? `${t.add} · ${formatMoney(price)}` : t.soldOut;
    }

    async add() {
      const v = this.variant();
      if (!v) return;
      this.submit.disabled = true;
      this.msg.classList.remove('is-error');
      try {
        await addToCart(v.id);
        markAdded(sheetBtn);
        if (window.Brera?.cart?.drawer && window.Brera.cart.openOnAdd) {
          /* Con carrito lateral: se cierra la hoja y se abre el carrito (assets/cart.js) */
          this.close();
        } else {
          this.msg.innerHTML = `${esc(t.added)} · <a href="${root}cart">${esc(t.viewCart)}</a>`;
          setTimeout(() => this.close(), 1400);
        }
      } catch (e) {
        this.msg.textContent = e.message || t.error;
        this.msg.classList.add('is-error');
      }
      this.submit.disabled = false;
    }
  }

  // ✓ en el botón por un momento después de agregar (el + de la tarjeta lo muestra; assets/product-card.css)
  const markAdded = (btn) => {
    if (!btn) return;
    btn.classList.add('is-added');
    setTimeout(() => btn.classList.remove('is-added'), 1800);
  };

  let sheet;
  let sheetBtn = null;
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    btn.classList.add('is-loading');
    try {
      if (btn.dataset.variantId) {
        await addToCart(btn.dataset.variantId);
        markAdded(btn);
        if (!(window.Brera?.cart?.drawer && window.Brera.cart.openOnAdd)) {
          toast(`${esc(t.added)} <a href="${root}cart">${esc(t.viewCart)}</a>`);
        }
      } else {
        sheet = sheet || new Sheet();
        sheetBtn = btn;
        await sheet.open(btn.dataset.handle, btn.dataset.url);
      }
    } catch (err) {
      toast(esc(err.message || t.error));
    }
    btn.classList.remove('is-loading');
  });

  window.BreraQuickAdd = { addToCart, formatMoney };
})();
